import {
  Runtime,
  analytics,
  useMixpanelFlag,
  useSiteInfo,
} from '@silvertours/front-entities';
import { useEffect, useMemo, useRef, useState } from 'react';

const EXPERIMENT_ID = 'app_install_banner_test';
const FLAG_KEY = 'hideAppInstallBanner';

/** Keeps the Mixpanel assignment, so that later pages need not wait for it. */
export const ASSIGNMENT_STORAGE_NAME = 'appBannerExperimentAssignment';
const ASSIGNMENT_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Time to wait for the Mixpanel assignment. After this time the banner shows
 * as before, and a late assignment has no effect on this page.
 */
export const ASSIGNMENT_TIMEOUT_MS = 2000;

type Variant = 'control' | 'hide_banner';

/**
 * Why the banner shows or does not show. The banner view event sends it, so
 * that a banner in the hide_banner variant is easy to find.
 */
export type ExperimentState =
  | 'not_in_test'
  | 'no_consent'
  | 'pending'
  | 'assigned'
  | 'stored'
  | 'timeout'
  | 'late';

type StoredAssignment = { variant: Variant; assignedAt: number };

const readStoredVariant = (): Variant | undefined => {
  try {
    const raw = window.localStorage.getItem(ASSIGNMENT_STORAGE_NAME);
    if (!raw) {
      return undefined;
    }

    const { variant, assignedAt } = JSON.parse(raw) as StoredAssignment;
    const isValid =
      (variant === 'control' || variant === 'hide_banner') &&
      Date.now() - assignedAt < ASSIGNMENT_MAX_AGE_MS;

    return isValid ? variant : undefined;
  } catch {
    return undefined;
  }
};

const storeVariant = (variant: Variant | undefined) => {
  try {
    if (variant) {
      const assignment: StoredAssignment = { variant, assignedAt: Date.now() };
      window.localStorage.setItem(
        ASSIGNMENT_STORAGE_NAME,
        JSON.stringify(assignment),
      );
    } else {
      window.localStorage.removeItem(ASSIGNMENT_STORAGE_NAME);
    }
  } catch {
    // Without storage, every page waits for Mixpanel as before.
  }
};

/**
 * A/B test for the app install banner (LPOT-1734). The Mixpanel flag
 * `hideAppInstallBanner` assigns the variant. The hook reads the flag, and
 * with it sends the Mixpanel exposure, only when the banner would show on this
 * page. The assignment is stored, so later pages do not wait for Mixpanel.
 * Without Mixpanel consent there is no assignment and the banner shows as
 * before.
 *
 * @param isEligible `true` when the banner would show on this page.
 * @param isBannerShown `true` when the banner shows or showed on this page.
 */
export const useAppBannerExperiment = (
  isEligible: boolean,
  isBannerShown: boolean,
) => {
  const { dynamicFeatureToggles } = useSiteInfo();
  const hasMixpanelConsent = Runtime.useConsent(
    Runtime.Services.mixpanelProfiles,
  );
  const [isTimedOut, setIsTimedOut] = useState(false);
  const isExposureTracked = useRef(false);

  const isInTest = isEligible && hasMixpanelConsent;
  const storedVariant = useMemo(
    () => (isInTest ? readStoredVariant() : undefined),
    [isInTest],
  );

  // With a stored assignment, the banner can show at once (control). The flag
  // is still read, so that both variants send the exposure and keep the
  // stored assignment up to date.
  const flag = useMixpanelFlag(
    FLAG_KEY,
    isInTest && (!isBannerShown || storedVariant !== undefined),
  );

  let flagVariant: Variant | undefined;
  if (flag.status === 'assigned') {
    flagVariant = flag.variant === 'control' ? 'control' : 'hide_banner';
  }

  useEffect(() => {
    if (flag.status !== 'loading') {
      storeVariant(flagVariant);
    }
  }, [flag.status, flagVariant]);

  // A toggle from the URL or from a manual push wins, for QA.
  const override = dynamicFeatureToggles.hideAppInstallBanner;

  let assignment: { variant: Variant; state: ExperimentState } | undefined;
  if (override !== undefined) {
    assignment = {
      variant: override ? 'hide_banner' : 'control',
      state: 'assigned',
    };
  } else if (flagVariant) {
    assignment = { variant: flagVariant, state: 'assigned' };
  } else if (storedVariant && flag.status !== 'missing') {
    assignment = { variant: storedVariant, state: 'stored' };
  }

  const isAwaitingAssignment =
    isInTest && !assignment && flag.status === 'loading';

  useEffect(() => {
    if (!isAwaitingAssignment || isTimedOut) {
      return undefined;
    }

    const timer = setTimeout(() => setIsTimedOut(true), ASSIGNMENT_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [isAwaitingAssignment, isTimedOut]);

  let variant: Variant | undefined;
  let experimentState: ExperimentState;
  if (!isEligible) {
    experimentState = 'not_in_test';
  } else if (!hasMixpanelConsent) {
    experimentState = 'no_consent';
  } else if (isTimedOut && assignment?.state !== 'stored') {
    // A late assignment has no effect on this page.
    experimentState = 'timeout';
  } else if (assignment) {
    ({ variant } = assignment);
    experimentState = assignment.state;
  } else if (flag.status === 'missing') {
    experimentState = 'not_in_test';
  } else {
    experimentState = 'pending';
  }

  // The user saw the banner before the assignment arrived, so the page cannot
  // use the hide_banner variant. This late assignment has no effect.
  if (isBannerShown && variant === 'hide_banner') {
    variant = undefined;
    experimentState = 'late';
  }

  // A stored assignment is not confirmed yet. Track the exposure when the flag
  // or a toggle from the URL gives the variant.
  useEffect(() => {
    if (variant && experimentState !== 'stored' && !isExposureTracked.current) {
      isExposureTracked.current = true;
      analytics.generic.trackExperimentViewed(EXPERIMENT_ID, variant);
    }
  }, [variant, experimentState]);

  return {
    isPending: experimentState === 'pending',
    isBannerHidden: variant === 'hide_banner',
    experimentState,
  };
};
